import React, { useMemo } from 'react'
import { graphql, PageProps } from 'gatsby'
import styled, { css } from 'styled-components'

import withLocalization from 'decorators/withLocalization'
import { Section } from 'components/Layout/styles'
import PageLayout from 'components/Layout/PageLayout'
import ContentBox from 'widgets/ContentBox'

import ProgramsSummary from 'modules/Homepage/ProgramsSummary'
import ProgramsIntro from 'modules/Homepage/ProgramsIntro'
import Reasons from 'modules/Homepage/Reasons'
import HeroBackground from 'modules/Homepage/HeroBackground'
import { DefaultHeadFC } from 'utils/helpers'
import Seo from 'components/shared/Seo'
import { breakpoint } from 'ui/elements/ThemeProvider'

const IntroText = styled.p`
  text-align: center;

  ${breakpoint.md(
    () => css`
      width: ${(7 / 12) * 100}%;
      margin-left: auto;
      margin-right: auto;
    `
  )}
`

export const Head: DefaultHeadFC<Queries.HomepageQuery> = ({
  location,
  pageContext,
  data,
}) => {
  const {
    i18n: { language },
  } = pageContext

  return (
    <Seo
      title={
        data.sanityIndex?.meta?.title?.localized ??
        'WHU – Otto Beisheim School of Management'
      }
      description={data.sanityIndex?.meta?.description?.localized ?? undefined}
      openGraph={{
        title: 'Homepage',
        url: `https://ee.whu.edu${location.pathname}`,
        locale: language,
      }}
      pageContext={pageContext}
      meta={data.sanityIndex?.meta}
    />
  )
}

const Homepage: React.FC<PageProps<Queries.HomepageQuery>> = ({
  data: {
    headerCTASiteConfig,
    infoBarConfig,
    sanityIndex: data,
    ...readProgramsSummary
  },
}) => {
  const heroProps = useMemo(() => {
    return {
      type: 'corner',
      variant: 'left-right',
      em: 'Excellence in Management Education',
      background: <HeroBackground />,
    }
  }, [])

  return (
    <PageLayout
      data={data ?? (undefined as any)}
      infoBarConfig={infoBarConfig}
      headerCTASiteConfig={headerCTASiteConfig}
      heroProps={heroProps}
    >
      <Section>
        <ProgramsIntro intro={data?.afterHero?.localized ?? undefined} />
      </Section>

      <Section>
        <IntroText>{data?.intro?.localized}</IntroText>
        <ProgramsSummary data={readProgramsSummary} />
      </Section>

      <Section>
        <Reasons />
      </Section>

      <Section>
        <ContentBox blocks={data?._rawContent} />
      </Section>
    </PageLayout>
  )
}

export const query = graphql`
  query Homepage($id: String, $language: String!) {
    sanityIndex(id: { eq: $id }) {
      hero {
        ...HeroFragment
      }
      meta {
        title {
          de
          en
          localized(locale: $language)
        }
        description {
          de
          en
          localized(locale: $language)
        }
      }
      afterHero {
        localized(locale: $language)
      }
      intro {
        localized(locale: $language)
      }
      _rawContent(resolveReferences: { maxDepth: 10 })
    }
    infoBarConfig: sanitySiteConfig {
      ...InfoBarConfig
    }

    ...PageLayoutConfig

    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }

    ...ReadProgramsSummary
  }
`

export default withLocalization(Homepage)
